<script setup lang="ts">
import type { TreeExpandedKeys, TreePassThroughMethodOptions } from 'primevue/tree';
import { ref, watch } from 'vue';

import type {
  ITreeEmits,
  ITreeItem,
  ITreeProps,
  ITreeSlotLeafNode,
  ITreeSlotParentNode,
} from './types';

const props = withDefaults(defineProps<ITreeProps>(), { expandedKeys: () => ({}) });
const emit = defineEmits<ITreeEmits>();
defineSlots<{
  parentNode: ITreeSlotParentNode;
  leafNode: ITreeSlotLeafNode;
}>();

const expandedKeysModel = ref<TreeExpandedKeys>(props.expandedKeys);

watch(
  () => props.expandedKeys,
  (val) => {
    expandedKeysModel.value = val || {};
  },
  { deep: true },
);

function handleExpand(value: TreeExpandedKeys): void {
  emit('update-expanded-keys', value);
}

function toggleButtonPT({ props: togglerProps }: TreePassThroughMethodOptions): Record<string, string> {
  if ('node' in togglerProps) {
    return (togglerProps.node as ITreeItem)?.children?.length
      ? { class: '!m-0 !p-0 p-button p-button-icon-only p-button-lg !text-primary h-8 w-8 !mr-1' }
      : { class: 'hidden' };
  }

  return {};
}
</script>

<template>
  <div v-data-qa-auto-id="'VTree'">
    <PTree
      v-model:expanded-keys="expandedKeysModel"
      :meta-key-selection="false"
      :value="items"
      :pt="{
        root: { class: '!p-0 !border-none' },
        rootChildren: { class: 'gap-0' },
        nodeContent: { class: 'min-h-[44px] border bg-highlight border-primary/30 mb-2 gap-0' },
        nodeLabel: { class: 'w-full' },
        nodeChildren: { class: 'xs:pl-2 sm:pl-6 gap-0 pt-0' },
        nodeToggleButton: toggleButtonPT,
      }"
      @update:expanded-keys="handleExpand"
    >
      <template #nodetoggleicon="{ expanded }">
        <i
          class="pi p-button-icon"
          :class="{ 'pi-folder-open': expanded, 'pi-folder': !expanded }"
        />
      </template>
      <template #default="{ node }">
        <!-- eslint-disable-next-line vue/no-undef-properties -->
        <template v-if="node?.children?.length">
          <!-- eslint-disable-next-line vue/max-attributes-per-line, vue/no-parsing-error -->
          <slot
            name="parentNode"
            :node="node"
          />
        </template>
        <template v-else>
          <!-- eslint-disable-next-line vue/max-attributes-per-line, vue/no-parsing-error -->
          <slot
            name="leafNode"
            :node="node"
          />
        </template>
      </template>
    </PTree>
  </div>
</template>
